import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

// Helpers
import { date } from 'helpers/format'

// Models
import Contract from 'models/Contract'

// Use
import useMounted from 'use/mounted'

// Components
import Box from 'ui/Box'
import Skeleton from 'ui/Skeleton'
import IconMoney from 'ui/Icons/IconMoney'
import Row from 'ui/Row'
import Col from 'ui/Col'

// Api
import { getContractSummary } from 'api/contract'

// Assets
import ImageProof from 'assets/images/proof.png'

// Styles
import './ContractSummary.scss'

export const ContractSummary = ({
  contract,
  contractNumber,
  vendorCode,
  companyCode,
  fullWidth,
}) => {
  const isMounted = useMounted()

  // States
  const [loading, setLoading] = useState(false)
  const [contractSummary, setContractSummary] = useState({
    total: 0,
    paid: 0,
    pending: 0,
  })

  useEffect(() => {
    if (contractNumber && vendorCode && companyCode) {
      setLoading(true)
      getContractSummary({
        contractNumber,
        vendorCode,
        companyCode,
      })
        .then((data) => {
          if (isMounted.current) {
            setContractSummary(data)
          }
        })
        .finally(() => {
          if (isMounted.current) {
            setLoading(false)
          }
        })
    }
  }, [companyCode, contractNumber, isMounted, vendorCode])

  const now = date(new Date(), 'MMMM yyyy')

  const effectRatioProcess = {
    paid:
      contractSummary.total > 0
        ? Math.floor((contractSummary.paid / contractSummary.total) * 100)
        : 0,
    pending:
      contractSummary.total > 0
        ? Math.floor((contractSummary.pending / contractSummary.total) * 100)
        : 0,
  }

  return (
    <Box style={{ borderRadius: 0 }}>
      <Skeleton loading={loading}>
        <Row>
          <Col xs={12} sm={12} md={fullWidth ? 12 : 10}>
            <div className="banner-tracker">
              <p className="tracker--title">Payment tracker</p>
              <div className="payment-content">
                <div className="payment-content__top columns is-multiline is-mobile">
                {
                  contract.contractPeriod ? (<div className="one --period column is-narrow">
                    <p className="label">Contract period:</p>
                    <p id="contract-period" className="value">
                      {contract.contractPeriod}
                    </p>
                  </div>) :<></>
                }
                  
                  <div className="one column is-narrow">
                    <p className="label">Payment term:</p>
                    <p id="payment-term" className="value">
                      {contract.paymentTerm}
                    </p>
                  </div>
                  <div className="one column is-narrow">
                    <p className="label">Current rate:</p>
                    <p id="payment-rate" className="value">
                      {contract.paymentRate}
                    </p>
                  </div>
                </div>
                <div className="payment-content__bottom">
                  <div className="--left">
                    <div className="total-payment">
                      <div className="total-payment__icon">
                        <IconMoney />
                      </div>
                      <div className="total-payment__content">
                        <p className="label">Total payments:</p>
                        <p id="total-payments" className="value">
                          {contractSummary.total}
                        </p>
                      </div>
                    </div>

                    <div className="payment-progress">
                      <div className="payment-progress__title">
                        <p className="--title">
                          Payment progress:
                          <span className="--des"> As of {now} </span>
                        </p>
                      </div>
                      <div className="payment-progress__progress">
                        <div className="decor"></div>
                        <span
                          className="--paid"
                          style={{ width: effectRatioProcess.paid + '%' }}
                        ></span>
                        <span
                          className="--pending"
                          style={{
                            width: effectRatioProcess.pending + '%',
                            marginLeft: contractSummary.paid ? -3 : 0,
                          }}
                        ></span>
                      </div>
                      <div className="payment-progress__sign">
                        <div className="progress--item">
                          <div className="--symbol paid"></div>
                          {contractSummary.paid} Paid
                        </div>
                        <div className="progress--item">
                          <div className="--symbol pending"></div>
                          {contractSummary.pending} pending
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="--right">
                    <img
                      draggable="false"
                      src={ImageProof}
                      alt="proof-image"
                      className="img"
                    />
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Skeleton>
    </Box>
  )
}

ContractSummary.propTypes = {
  contractNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  vendorCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  companyCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  contract: PropTypes.instanceOf(Contract),
  fullWidth: PropTypes.bool,
}
ContractSummary.defaultProps = {
  contractNumber: '',
  vendorCode: '',
  companyCode: '',
  contract: new Contract(),
  fullWidth: true,
}

export default ContractSummary
